// Parses every exportable GFM table out of a markdown *string* by
// reproducing the chat renderer's remark/rehype pipeline headlessly, then
// reusing the same hast-based `parseMarkdownTable` the renderer uses. This
// is the only supported path: neither `react-markdown` nor
// `@assistant-ui/react-markdown` exposes a reusable processor.
import type { ElementContent as HastContent, Root as HastRoot } from "hast";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import { unified } from "unified";

import {
    inferTitleFromSiblings,
    parseMarkdownTable,
    type ChatTableData,
} from "./parse-markdown-table";

// Mirrors `markdown-text.tsx`'s plugin chain for non-render parsing.
//
// `allowDangerousHtml: true` MUST be set on `remark-rehype`:
// `react-markdown@10` hard-codes it internally, so this standalone chain
// inherits nothing. Without it, raw HTML is dropped at the mdast→hast
// bridge *before* `rehype-raw` runs, and `parseMarkdownTable`'s in-cell
// `<span data-status/data-bool>` tags and `data-custom-href` source links
// are silently stripped, so tables would export without tags or hyperlinks
// while still looking correct. `rehype-raw` runs *after* `remark-rehype`
// to promote that raw HTML into real hast elements.
//
// Deliberately omitted vs. the render path: `rehypeInferTableTitle` (its
// only job is render-time tree mutation; we call the pure
// `inferTitleFromSiblings` directly instead) and `rehype-sanitize` (not in
// the chat pipeline; gatekeeping there is react-markdown's
// `disallowedElements` + `urlTransform`, so adding it would diverge).
//
// The processor holds no per-document state, so a module-scope singleton is
// safe to reuse across calls.
const processor = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw);

/**
 * Parse all exportable tables from a single markdown string, in document
 * order. Each table's title is the nearest preceding heading (via the same
 * `inferTitleFromSiblings` the renderer uses). Non-exportable tables
 * (no thead, merged cells, blank/empty body) are dropped; `parseMarkdownTable`
 * returns `null` for those.
 */
export function parseTablesFromMarkdown(markdown: string): ChatTableData[] {
    if (!markdown) return [];
    const tree = processor.runSync(processor.parse(markdown)) as HastRoot;
    // `tree.children` is `RootContent[]` (a superset including `Doctype`);
    // the cast matches `markdown-text.tsx` and is safe because we only
    // inspect element/text nodes below.
    const children = (tree.children ?? []) as HastContent[];

    const tables: ChatTableData[] = [];
    for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child.type !== "element" || child.tagName !== "table") continue;
        const data = parseMarkdownTable(child);
        if (!data) continue;
        const found = inferTitleFromSiblings(children, i);
        tables.push(found ? { ...data, title: found.title } : data);
    }
    return tables;
}
